import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiCheckCircle, FiEye, FiPlus, FiSearch, FiShieldOff, FiTrash2 } from "react-icons/fi";
import bankService from "../../../services/bank.service";
import PaginationBar from "../../../components/common/PaginationBar";
import StatusFilterSelect from "../../../components/common/StatusFilterSelect";
import { PAGE_LIMIT } from "../../../constants/pagination";
import "../../../styles/users.css";

const BANK_STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "pending_payment", label: "Pending" },
  { value: "active", label: "Active" },
  { value: "expired", label: "Expired" },
  { value: "inactive", label: "Blocked" },
];

function bankStatusLabel(status) {
  const s = String(status || "").toLowerCase();
  if (s === "pending_payment") return "Pending";
  if (s === "active") return "Active";
  if (s === "expired") return "Expired";
  if (s === "inactive") return "Blocked";
  return status ? status.charAt(0).toUpperCase() + status.slice(1) : "—";
}

function bankStatusClass(status) {
  if (status === "pending_payment") return "pending";
  if (status === "inactive") return "inactive";
  return String(status || "inactive").toLowerCase();
}

export default function SsdiBanks() {
  const [banks, setBanks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [deletingIds, setDeletingIds] = useState(() => new Set());
  const [statusIds, setStatusIds] = useState(() => new Set());
  const LIMIT = PAGE_LIMIT;

  const load = async (p = page, s = appliedSearch, st = statusFilter) => {
    setLoading(true);
    try {
      const res = await bankService.ssdiListBanks({
        page: p,
        limit: LIMIT,
        search: s || undefined,
        status: st || undefined,
      });
      setBanks(res?.data?.data?.banks || []);
      setTotal(res?.data?.data?.total || 0);
    } catch {
      setBanks([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(page, appliedSearch, statusFilter);
  }, [page, statusFilter, appliedSearch]);

  const handleUpdateStatus = async (bank, status) => {
    const bankId = bank?._id;
    if (!bankId || statusIds.has(bankId)) return;
    const label = bank.bankName || bank.bankCode || "this bank / NBFC";
    const confirmMessage =
      status === "active"
        ? `Activate ${label}? The bank / NBFC admin will be able to log in.`
        : `Block ${label}? Bank / NBFC users will not be able to log in.`;
    if (!window.confirm(confirmMessage)) return;

    setStatusIds((prev) => new Set(prev).add(bankId));
    try {
      const res = await bankService.ssdiUpdateStatus(bankId, { status });
      const updated = res?.data?.data?.bank || res?.data?.bank;
      setBanks((prev) =>
        prev.map((item) =>
          item._id === bankId
            ? { ...item, ...(updated || {}), status }
            : item
        )
      );
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to update bank / NBFC status");
    } finally {
      setStatusIds((prev) => {
        const next = new Set(prev);
        next.delete(bankId);
        return next;
      });
    }
  };

  const handleDeleteBank = async (bank) => {
    const bankId = bank?._id;
    if (!bankId || deletingIds.has(bankId)) return;
    const label = bank.bankName || bank.bankCode || "this bank / NBFC";
    if (
      !window.confirm(
        `Delete ${label}? This permanently removes the bank / NBFC account, users, uploads, and related data. This cannot be undone.`
      )
    ) {
      return;
    }

    setDeletingIds((prev) => new Set(prev).add(bankId));
    try {
      await bankService.ssdiDeleteBank(bankId);
      setBanks((prev) => prev.filter((item) => item._id !== bankId));
      setTotal((prev) => Math.max(0, prev - 1));
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to delete bank / NBFC");
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(bankId);
        return next;
      });
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setAppliedSearch(search.trim());
  };

  const totalPages = Math.ceil(total / LIMIT) || 0;
  const emptyMessage = appliedSearch.trim()
    ? `No banks / NBFCs match “${appliedSearch.trim()}”.`
    : "No banks / NBFCs found.";

  return (
    <div className="page companies-page">
      <div className="users-actions">
        <h2>Banks / NBFCs ({total})</h2>
        <Link to="/ssdi/banks/create" className="primary-page-btn">
          <FiPlus aria-hidden /> Create Bank / NBFC
        </Link>
      </div>

      <form className="company-search-panel company-search-panel--simple" onSubmit={handleSearch}>
        <div className="company-search-bar">
          <FiSearch className="company-search-icon" aria-hidden />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, code, email..."
            aria-label="Search banks / NBFCs"
          />
        </div>
        <StatusFilterSelect
          value={statusFilter}
          onChange={(value) => {
            setStatusFilter(value);
            setPage(1);
          }}
          options={BANK_STATUS_OPTIONS}
          ariaLabel="Filter banks / NBFCs by status"
        />
        <button type="submit" className="primary-page-btn">
          Search
        </button>
      </form>

      {loading && banks.length === 0 ? (
        <p className="cf-excel-loading">Loading banks / NBFCs…</p>
      ) : (
        <div className="company-table-wrap ssdi-excel-sheet table-scroll printable-table-area">
          <table className="users-table excel-grid-table">
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Bank / NBFC Name</th>
                <th>Code</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Next Due</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {banks.length === 0 ? (
                <tr>
                  <td colSpan={8}>{emptyMessage}</td>
                </tr>
              ) : (
                banks.map((bank, index) => (
                  <tr key={bank._id} className={deletingIds.has(bank._id) ? "row-pending" : ""}>
                    <td>
                      {Math.max(total - (page - 1) * LIMIT - index, 1)}
                    </td>
                    <td>{bank.bankName || "—"}</td>
                    <td>{bank.bankCode || "—"}</td>
                    <td>{bank.email || "—"}</td>
                    <td>{bank.phone || "—"}</td>
                    <td>
                      <span
                        className={`company-status company-status--${bankStatusClass(bank.status)}`}
                      >
                        {bankStatusLabel(bank.status)}
                      </span>
                    </td>
                    <td>
                      {bank.nextDueAt
                        ? new Date(bank.nextDueAt).toLocaleDateString("en-IN")
                        : "—"}
                    </td>
                    <td className="company-actions-td">
                      <div className="company-actions-cell">
                        <Link
                          to={`/ssdi/banks/${bank._id}`}
                          className="company-btn company-btn--view"
                        >
                          <FiEye aria-hidden />
                          <span>Manage</span>
                        </Link>
                        {bank.status === "active" ? (
                          <button
                            type="button"
                            className="company-btn company-btn--block"
                            disabled={statusIds.has(bank._id)}
                            onClick={() => handleUpdateStatus(bank, "inactive")}
                          >
                            <FiShieldOff aria-hidden />
                            <span>{statusIds.has(bank._id) ? "Blocking…" : "Block"}</span>
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="company-btn company-btn--unblock"
                            disabled={statusIds.has(bank._id)}
                            onClick={() => handleUpdateStatus(bank, "active")}
                          >
                            <FiCheckCircle aria-hidden />
                            <span>{statusIds.has(bank._id) ? "Activating…" : "Active"}</span>
                          </button>
                        )}
                        <button
                          type="button"
                          className="company-btn company-btn--delete"
                          disabled={deletingIds.has(bank._id)}
                          onClick={() => handleDeleteBank(bank)}
                        >
                          <FiTrash2 aria-hidden />
                          <span>{deletingIds.has(bank._id) ? "Deleting…" : "Delete"}</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <PaginationBar
        page={page}
        totalPages={totalPages}
        total={total}
        loading={loading}
        onPageChange={setPage}
      />
    </div>
  );
}
